import { useState, useEffect, useRef } from 'react'
import { request } from '@/lib/request'
import { DateTimePicker } from '@/components/DateTimePicker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import html2canvas from 'html2canvas'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { usePersistentState } from '@/hooks/usePersistentState'

dayjs.extend(customParseFormat)

const dateFormat = 'YYYY-MM-DD HH:mm'

interface FlowItem {
  name: string
  currency: number
  cost: number
  costs: Array<{ date: string; currency: number }>
  detail: Array<{ date: string; currency: number }>
}

interface OrderInfo {
  profit?: number
  total?: number
  costs?: number
  profit_rate?: string
  flows?: FlowItem[]
}

interface User {
  username: string
}

export default function Order() {
  const defaultDate = dayjs().date(3).hour(13).minute(0).toDate()
  const [filters, setFilters, clearFilters] = usePersistentState('admin:order:filters', () => ({
    date: dayjs(defaultDate).format(dateFormat),
    user: undefined as string | undefined,
  }))
  const [info, setInfo] = useState<OrderInfo>({})
  const [users, setUsers] = useState<User[]>([])
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const getList = async () => {
    try {
      const resp = await request('/common/apis/v1/tmp_order', filters)
      if (resp && resp.code === 0) {
        setInfo(resp.data)
      }
    } catch (error) {
      console.error('Failed to load order data:', error)
    }
  }

  const getUsers = async () => {
    try {
      const resp = await request('/system/api/query/user', {
        query_filter: {},
        page_num: 1,
        page_size: 100,
      })
      if (resp && resp.code === 0) {
        setUsers(resp.data.list || [])
      }
    } catch (error) {
      console.error('Failed to load users:', error)
    }
  }

  useEffect(() => {
    getList()
  }, [filters])

  useEffect(() => {
    getUsers()
  }, [])

  const handleDownload = () => {
    if (contentRef.current) {
      html2canvas(contentRef.current).then((canvas) => {
        const link = document.createElement('a')
        link.download = `order-${new Date().toISOString()}.png`
        link.href = canvas.toDataURL()
        link.click()
      })
    }
  }

  const renderTimeline = (record: FlowItem) => {
    const timelineData: Record<string, { date: string; currency: number; type: '+' | '-' }> = {}
    const dates: string[] = []

    record.costs.forEach((val) => {
      timelineData[val.date] = { ...val, type: '-' }
      dates.push(val.date)
    })

    record.detail.forEach((val) => {
      timelineData[val.date] = { ...val, type: '+' }
      dates.push(val.date)
    })

    const sortedDates = dates.sort()

    return (
      <div className="p-4 bg-muted/50 rounded-md">
        <div className="relative pl-6 space-y-4">
          {sortedDates.map((date, index) => {
            const item = timelineData[date]
            return (
              <div key={index} className="relative">
                <div
                  className={`absolute left-[-1.25rem] top-2 w-3 h-3 rounded-full border-2 ${
                    item.type === '-'
                      ? 'bg-red-500 border-red-500'
                      : 'bg-green-500 border-green-500'
                  }`}
                />
                {index < sortedDates.length - 1 && (
                  <div className="absolute left-[-0.6875rem] top-5 bottom-[-1rem] w-0.5 bg-border" />
                )}
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground min-w-[140px]">{item.date}</span>
                  <span
                    className={`font-medium ${
                      item.type === '-' ? 'text-red-500' : 'text-green-500'
                    }`}
                  >
                    {item.type}
                    {item.currency}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <h1 className="text-2xl font-bold mb-6">订单管理</h1>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">时间:</span>
          <DateTimePicker
            value={dayjs(filters.date, dateFormat).toDate()}
            onChange={(date) => {
              if (date) {
                setFilters({ ...filters, date: dayjs(date).format(dateFormat) })
              }
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">用户:</span>
          <Select
            value={filters.user || ''}
            onValueChange={(value) => setFilters({ ...filters, user: value })}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="选择用户" />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem key={user.username} value={user.username}>
                  {user.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" onClick={clearFilters}>
          重置
        </Button>
        <Button onClick={handleDownload}>下载</Button>
      </div>

      <div ref={contentRef} className="bg-background">
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {filters.user}:{filters.date} → {new Date().toLocaleString()}
          </h2>
          <div className="flex gap-6 text-lg">
            <span className="text-blue-600">利润: {info.profit || 0}</span>
            <span className="text-red-600">总额: {info.total || 0}</span>
            <span className="text-green-600">成本: {info.costs || 0}</span>
            <span className="text-blue-600">利润率: {info.profit_rate || '0%'}</span>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>名称</TableHead>
                <TableHead>货币</TableHead>
                <TableHead>成本</TableHead>
                <TableHead>总计</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {info.flows?.map((record, index) => (
                <>
                  <TableRow key={index} className="cursor-pointer hover:bg-muted/50">
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                      >
                        {expandedRow === index ? '▼' : '▶'}
                      </Button>
                    </TableCell>
                    <TableCell>{record.name}</TableCell>
                    <TableCell>{record.currency}</TableCell>
                    <TableCell>{record.cost}</TableCell>
                    <TableCell>{record.currency - record.cost}</TableCell>
                  </TableRow>
                  {expandedRow === index && (
                    <TableRow>
                      <TableCell colSpan={5} className="p-0">
                        {renderTimeline(record)}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}
